import React, { useState, useMemo } from 'react'
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, MoreVertical, Edit, Trash2, Eye, Download, Filter } from 'lucide-react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './table'
import { Button } from './button'
import { Input } from './input'

export interface DataTableColumn<T> {
  key: string
  label: string
  width?: string
  sortable?: boolean
  render?: (value: any, row: T) => React.ReactNode
  align?: 'left' | 'center' | 'right'
}

export interface DataTableAction<T> {
  label: string
  icon?: React.ReactNode
  onClick: (row: T) => void
  variant?: 'default' | 'destructive' | 'outline' | 'secondary' | 'ghost' | 'link'
  className?: string
}

export interface DataTableProps<T> {
  columns: DataTableColumn<T>[]
  data: T[]
  actions?: DataTableAction<T>[]
  pagination?: {
    current: number
    total: number
    pageSize: number
    onPageChange: (page: number) => void
    onPageSizeChange?: (size: number) => void
  }
  sort?: {
    column: string
    direction: 'asc' | 'desc'
    onSortChange: (column: string, direction: 'asc' | 'desc') => void
  }
  selection?: {
    selectedRows: T[]
    onSelectionChange: (selectedRows: T[]) => void
    getRowId: (row: T) => string | number
  }
  loading?: boolean
  emptyMessage?: string
  search?: {
    placeholder?: string
    onSearch: (query: string) => void
    value?: string
  }
  className?: string
  rowClassName?: (row: T, index: number) => string
  onRowClick?: (row: T) => void
}

const DataTable = <T extends Record<string, any>>({
  columns,
  data,
  actions = [],
  pagination,
  sort,
  selection,
  loading = false,
  emptyMessage = 'Aucune donnée disponible',
  search,
  className = '',
  rowClassName,
  onRowClick,
}: DataTableProps<T>) => {
  const [localSearch, setLocalSearch] = useState('')
  const [showActionsMenu, setShowActionsMenu] = useState<string | number | null>(null)

  // Gestion de la recherche locale si pas de gestion externe
  const filteredData = useMemo(() => {
    if (!search?.value && !localSearch) return data
    
    const query = search?.value || localSearch.toLowerCase()
    if (!query) return data
    
    return data.filter(row => {
      return columns.some(column => {
        const value = row[column.key]
        if (value == null) return false
        
        return String(value).toLowerCase().includes(query)
      })
    })
  }, [data, columns, search?.value, localSearch])

  // Déterminer si on doit afficher la barre de recherche
  // Afficher seulement si search est explicitement fourni (même objet vide)
  const shouldShowSearch = search !== undefined

  // Gestion du tri
  const sortedData = useMemo(() => {
    if (!sort) return filteredData
    
    const { column, direction } = sort
    return [...filteredData].sort((a, b) => {
      const aValue = a[column]
      const bValue = b[column]
      
      if (aValue == null && bValue == null) return 0
      if (aValue == null) return direction === 'asc' ? -1 : 1
      if (bValue == null) return direction === 'asc' ? 1 : -1
      
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return direction === 'asc' 
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue)
      }
      
      return direction === 'asc'
        ? (aValue < bValue ? -1 : aValue > bValue ? 1 : 0)
        : (aValue > bValue ? -1 : aValue < bValue ? 1 : 0)
    })
  }, [filteredData, sort])

  // Données paginées
  const paginatedData = useMemo(() => {
    if (!pagination) return sortedData
    
    const { current, pageSize } = pagination
    const startIndex = (current - 1) * pageSize
    return sortedData.slice(startIndex, startIndex + pageSize)
  }, [sortedData, pagination])

  // Gestion de la sélection
  const handleRowSelect = (row: T, checked: boolean) => {
    if (!selection) return
    
    const { selectedRows, onSelectionChange, getRowId } = selection
    const rowId = getRowId(row)
    
    const newSelectedRows = checked
      ? [...selectedRows, row]
      : selectedRows.filter(r => getRowId(r) !== rowId)
    
    onSelectionChange(newSelectedRows)
  }

  const handleSelectAll = (checked: boolean) => {
    if (!selection) return
    
    const { onSelectionChange } = selection
    onSelectionChange(checked ? [...data] : [])
  }

  // Gestion du tri
  const handleSort = (columnKey: string) => {
    if (!sort || !sort.onSortChange) return
    
    const { column, direction } = sort
    if (column === columnKey) {
      sort.onSortChange(columnKey, direction === 'asc' ? 'desc' : 'asc')
    } else {
      sort.onSortChange(columnKey, 'asc')
    }
  }

  // Gestion de la recherche
  const handleSearch = (value: string) => {
    if (search?.onSearch) {
      search.onSearch(value)
    } else {
      setLocalSearch(value)
    }
  }

  // Rendu des actions en ligne
  const renderRowActions = (row: T, rowIndex: number) => {
    if (actions.length === 0) return null
    
    const rowId = selection?.getRowId?.(row) || rowIndex
    
    return (
      <div className="relative">
        <Button
          variant="ghost"
          size="sm"
          className="h-8 w-8 p-0"
          onClick={() => setShowActionsMenu(showActionsMenu === rowId ? null : rowId)}
        >
          <MoreVertical className="h-4 w-4" />
        </Button>
        
        {showActionsMenu === rowId && (
          <div className="absolute right-0 top-full z-10 mt-1 w-48 rounded-md border bg-white shadow-lg">
            <div className="py-1">
              {actions.map((action, index) => (
                <button
                  key={index}
                  className={`flex w-full items-center px-3 py-2 text-sm hover:bg-gray-100 ${
                    action.variant === 'destructive' ? 'text-red-600' : 'text-gray-700'
                  }`}
                  onClick={() => {
                    action.onClick(row)
                    setShowActionsMenu(null)
                  }}
                >
                  {action.icon && <span className="mr-2">{action.icon}</span>}
                  {action.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    )
  }

  // Rendu du contenu
  const renderContent = () => {
    if (loading) {
      return (
        <TableRow>
          <TableCell colSpan={columns.length + (selection ? 1 : 0) + (actions.length > 0 ? 1 : 0)} className="h-32 text-center">
            <div className="flex flex-col items-center justify-center">
              <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent"></div>
              <p className="mt-2 text-sm text-gray-500">Chargement...</p>
            </div>
          </TableCell>
        </TableRow>
      )
    }

    if (paginatedData.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={columns.length + (selection ? 1 : 0) + (actions.length > 0 ? 1 : 0)} className="h-32 text-center">
            <div className="flex flex-col items-center justify-center">
              <div className="rounded-full bg-gray-100 p-3">
                <Filter className="h-6 w-6 text-gray-400" />
              </div>
              <p className="mt-2 text-sm text-gray-500">{emptyMessage}</p>
              {search && (
                <p className="mt-1 text-xs text-gray-400">
                  Essayez de modifier vos critères de recherche
                </p>
              )}
            </div>
          </TableCell>
        </TableRow>
      )
    }

    return paginatedData.map((row, rowIndex) => {
      const rowId = selection?.getRowId?.(row) || rowIndex
      const isSelected = selection?.selectedRows.some(r => selection.getRowId(r) === rowId) || false
      
      return (
        <TableRow
          key={rowId}
          className={`
            ${onRowClick ? 'cursor-pointer hover:bg-gray-50' : ''}
            ${rowClassName ? rowClassName(row, rowIndex) : ''}
            ${isSelected ? 'bg-primary-50' : ''}
          `}
          onClick={() => onRowClick?.(row)}
        >
          {selection && (
            <TableCell className="w-12">
              <input
                type="checkbox"
                checked={isSelected}
                onChange={(e) => handleRowSelect(row, e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                onClick={(e) => e.stopPropagation()}
              />
            </TableCell>
          )}
          
          {columns.map((column) => {
            const value = row[column.key]
            const content = column.render
              ? column.render(value, row)
              : value != null
                ? String(value)
                : '-'
            
            return (
              <TableCell
                key={column.key}
                className={column.align === 'center' ? 'text-center' : column.align === 'right' ? 'text-right' : 'text-left'}
                style={column.width ? { width: column.width } : undefined}
              >
                {content}
              </TableCell>
            )
          })}
          
          {actions.length > 0 && (
            <TableCell className="w-16 text-right">
              {renderRowActions(row, rowIndex)}
            </TableCell>
          )}
        </TableRow>
      )
    })
  }

  // Rendu de la pagination
  const renderPagination = () => {
    if (!pagination) return null
    
    const { current, total, pageSize } = pagination
    const totalPages = Math.ceil(total / pageSize)
    const startItem = (current - 1) * pageSize + 1
    const endItem = Math.min(current * pageSize, total)
    
    return (
      <div className="flex items-center justify-between px-4 py-3">
        <div className="text-sm text-gray-700">
          Affichage de <span className="font-medium">{startItem}</span> à{' '}
          <span className="font-medium">{endItem}</span> sur{' '}
          <span className="font-medium">{total}</span> résultats
        </div>
        
        <div className="flex items-center space-x-2">
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-700">Lignes par page:</span>
            {pagination.onPageSizeChange ? (
              <select
                value={pageSize}
                onChange={(e) => pagination.onPageSizeChange?.(Number(e.target.value))}
                className="h-8 rounded border border-gray-300 bg-white px-2 text-sm"
              >
                {[10, 25, 50, 100].map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            ) : (
              <span className="text-sm font-medium">{pageSize}</span>
            )}
          </div>
          
          <div className="flex items-center space-x-1">
            <Button
              variant="outline"
              size="sm"
              className="h-8 w-8 p-0"
              onClick={() => pagination.onPageChange(1)}
              disabled={current === 1}
            >
              <ChevronsLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-8 w-8 p-0"
              onClick={() => pagination.onPageChange(current - 1)}
              disabled={current === 1}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            
            <div className="flex items-center space-x-1">
              <span className="px-2 text-sm text-gray-700">
                Page {current} sur {totalPages}
              </span>
            </div>
            
            <Button
              variant="outline"
              size="sm"
              className="h-8 w-8 p-0"
              onClick={() => pagination.onPageChange(current + 1)}
              disabled={current === totalPages}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-8 w-8 p-0"
              onClick={() => pagination.onPageChange(totalPages)}
              disabled={current === totalPages}
            >
              <ChevronsRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={`rounded-lg border bg-white ${className}`}>
      {/* Barre de recherche */}
      {shouldShowSearch && (
        <div className="border-b px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <Input
                placeholder={search?.placeholder || 'Rechercher...'}
                value={search?.value || localSearch}
                onChange={(e) => handleSearch(e.target.value)}
                className="max-w-sm"
              />
            </div>
            
            {selection && selection.selectedRows.length > 0 && (
              <div className="ml-4 text-sm text-gray-700">
                {selection.selectedRows.length} élément(s) sélectionné(s)
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Tableau */}
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              {selection && (
                <TableHead className="w-12">
                  <input
                    type="checkbox"
                    checked={selection.selectedRows.length === data.length && data.length > 0}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                </TableHead>
              )}
              
              {columns.map((column) => (
                <TableHead
                  key={column.key}
                  className={`
                    ${column.sortable ? 'cursor-pointer hover:bg-gray-50' : ''}
                    ${column.align === 'center' ? 'text-center' : column.align === 'right' ? 'text-right' : 'text-left'}
                  `}
                  style={column.width ? { width: column.width } : undefined}
                  onClick={() => column.sortable && handleSort(column.key)}
                >
                  <div className="flex items-center justify-between">
                    <span>{column.label}</span>
                    {sort && sort.column === column.key && (
                      <span className="ml-1">
                        {sort.direction === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </div>
                </TableHead>
              ))}
              
              {actions.length > 0 && (
                <TableHead className="w-16 text-right">Actions</TableHead>
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {renderContent()}
          </TableBody>
        </Table>
      </div>
      
      {/* Pagination */}
      {renderPagination()}
    </div>
  )
}

// Composants d'action prédéfinis
export const EditAction = <T extends Record<string, any>>(
  onClick: (row: T) => void
): DataTableAction<T> => ({
  label: 'Modifier',
  icon: <Edit className="h-4 w-4" />,
  onClick,
  variant: 'default',
})

export const DeleteAction = <T extends Record<string, any>>(
  onClick: (row: T) => void
): DataTableAction<T> => ({
  label: 'Supprimer',
  icon: <Trash2 className="h-4 w-4" />,
  onClick,
  variant: 'destructive',
})

export const ViewAction = <T extends Record<string, any>>(
  onClick: (row: T) => void
): DataTableAction<T> => ({
  label: 'Voir',
  icon: <Eye className="h-4 w-4" />,
  onClick,
  variant: 'outline',
})

export const DownloadAction = <T extends Record<string, any>>(
  onClick: (row: T) => void
): DataTableAction<T> => ({
  label: 'Télécharger',
  icon: <Download className="h-4 w-4" />,
  onClick,
  variant: 'outline',
})

export default DataTable